import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import config from '../config';

// Initial State
const initialState = {
  employeeData: [],
  selectedEmployee: null,
  certifications: [],
  employeeSkills: [],
  employeeUtilization: [],
  employeeSummary: [], // ✅ NEW
  loading: false,
  error: null,
};

// ✅ Thunk: Fetch Active Employees
export const fetchEmployeeData = createAsyncThunk(
  'employeeData/fetchEmployeeData',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.get(`${config.apiURL}/activeemployees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching employee data:', error);
      return rejectWithValue(
        error.response?.data || 'Failed to fetch employee data'
      );
    }
  }
);

// ✅ Thunk: Fetch Certifications
export const fetchEmployeeCertifications = createAsyncThunk(
  'employeeData/fetchEmployeeCertifications',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.get(`${config.apiURL}/empcertification`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching certifications:', error);
      return rejectWithValue(
        error.response?.data || 'Failed to fetch certifications'
      );
    }
  }
);

// ✅ Thunk: Fetch Skills
export const fetchEmployeeSkillsById = createAsyncThunk(
  'employeeData/fetchEmployeeSkillsById',
  async (employeeId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.get(
        `${config.apiURL}/employee-skills/${employeeId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching employee skills:', error);
      return rejectWithValue(
        error.response?.data || 'Failed to fetch employee skills'
      );
    }
  }
);

// ✅ Thunk: Fetch Utilization
export const fetchEmployeeUtilizationById = createAsyncThunk(
  'employeeData/fetchEmployeeUtilizationById',
  async ({ employeeId, months }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const query = months
        .map((m) => `months=${encodeURIComponent(m)}`)
        .join('&');
      const response = await axios.get(
        `${config.apiURL}/employee-ultization/${employeeId}?${query}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching employee utilization:', error);
      return rejectWithValue(
        error.response?.data || 'Failed to fetch employee utilization'
      );
    }
  }
);

// ✅ Thunk: Fetch Employee Summary (NEW)
export const fetchEmployeeSummary = createAsyncThunk(
  'employeeData/fetchEmployeeSummary',
  async ({ months }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const query = months
        .map((m) => `months=${encodeURIComponent(m)}`)
        .join('&');
      const response = await axios.get(
        `${config.apiURL}/employee-summary?${query}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching employee summary:', error);
      return rejectWithValue(
        error.response?.data || 'Failed to fetch employee summary'
      );
    }
  }
);

// Slice
const employeeDataSlice = createSlice({
  name: 'employeeData',
  initialState,
  reducers: {
    resetEmployeeData: (state) => {
      state.employeeData = [];
      state.selectedEmployee = null;
      state.certifications = [];
      state.employeeSkills = [];
      state.employeeUtilization = [];
      state.employeeSummary = []; // ✅ reset summary
      state.loading = false;
      state.error = null;
    },
    setSelectedEmployee: (state, action) => {
      state.selectedEmployee = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Active Employees
      .addCase(fetchEmployeeData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeData.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeData = action.payload;
        if (action.payload.length > 0 && !state.selectedEmployee) {
          state.selectedEmployee = action.payload[0];
        }
      })
      .addCase(fetchEmployeeData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Certifications
      .addCase(fetchEmployeeCertifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeCertifications.fulfilled, (state, action) => {
        state.loading = false;
        state.certifications = action.payload;
      })
      .addCase(fetchEmployeeCertifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Employee Skills
      .addCase(fetchEmployeeSkillsById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeSkillsById.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeSkills = action.payload;
      })
      .addCase(fetchEmployeeSkillsById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Employee Utilization
      .addCase(fetchEmployeeUtilizationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeUtilizationById.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeUtilization = action.payload;
      })
      .addCase(fetchEmployeeUtilizationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Employee Summary
      .addCase(fetchEmployeeSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeSummary = action.payload;
      })
      .addCase(fetchEmployeeSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// ✅ Selectors
export const selectSelectedEmployeeCertifications = (state) => {
  const selected = state.employeeData.selectedEmployee;
  if (!selected || !selected.OFFICIAL_EMAIL_ID) return [];
  return state.employeeData.certifications.filter(
    (cert) => cert.EMAIL_ID === selected.OFFICIAL_EMAIL_ID
  );
};

export const selectEmployeeSkills = (state) =>
  state.employeeData.employeeSkills;

export const selectEmployeeUtilization = (state) =>
  state.employeeData.employeeUtilization;

export const selectEmployeeSummary = (state) =>
  state.employeeData.employeeSummary; // ✅ NEW

// Exports
export const { resetEmployeeData, setSelectedEmployee } =
  employeeDataSlice.actions;

export default employeeDataSlice.reducer;
